<template>
  <div id="whale-backtester-results">
    <v-card v-if="!results">
      <v-card-text class="text-body-2"> Backtest not run yet. </v-card-text>
    </v-card>

    <v-container v-else fluid pa-0>
      <!-- Buttons: Excel, Start/Stop tracking -->
      <v-row>
        <v-col cols="12">
          <v-btn
            :href="`/generated_backtesters/${results.excel_file}`"
            :small="$vuetify.display.xs"
            target="_blank"
            color="primary"
            class="ml-0"
          >
            Download Backtest Data to Excel
          </v-btn>
          <v-btn
            :href="`/generated_backtesters/${results.transaction_log}`"
            :small="$vuetify.display.xs"
            target="_blank"
            color="primary"
            class="ml-0"
          >
            Download Backtest Transaction Log
          </v-btn>
        </v-col>
      </v-row>

      <!-- Timeframe, Moving averages, Index selection, Backtest chart -->
      <v-row>
        <v-col cols="12">
          <v-card>
            <!-- Timeframe, Index selection, Moving averages -->
            <v-card-text>
              <v-container fluid pa-0>
                <v-row align="center">
                  <!-- Indices & Moving averages -->
                  <v-col cols="12" xl="8" order-xl="2">
                    <v-row align="center" justify="end">
                      <!-- Index selection -->
                      <v-col cols="12" order-xl="2">
                        <v-row align="center">
                          <v-col>
                            <v-select
                              v-model="chart.xAxis"
                              :items="chartIntervalItems"
                              label="Interval"
                              item-title="description"
                              return-object
                              hide-details
                              class="ma-0"
                              style="max-width: 150px"
                              @update:model-value="refresh()"
                            />
                          </v-col>
                          <v-col>
                            <v-select
                              v-model="chart.comparisonIndex"
                              :items="indices"
                              label="Comparison index"
                              item-title="description"
                              return-object
                              hide-details
                              class="ma-0"
                              style="max-width: 300px"
                              @update:model-value="refresh()"
                            />
                          </v-col>
                          <v-col>
                            <v-checkbox
                              v-model="chart.showPercentLong"
                              label="Show % Long"
                              @update:model-value="refresh()"
                            />
                          </v-col>
                          <v-col>
                            <v-checkbox
                              v-model="chart.showLog"
                              label="Toggle Logarithmic chart"
                              @update:model-value="refresh()"
                            />
                          </v-col>
                        </v-row>
                      </v-col>

                      <!-- Moving averages -->
                      <v-col :mr-4="$vuetify.display.xl" order-xl="1">
                        <v-btn
                          variant="outlined"
                          small
                          class="ma-0"
                          @click="movingAveragesDialog = true"
                        >
                          Configure moving averages
                        </v-btn>
                      </v-col>
                    </v-row>
                  </v-col>
                </v-row>
                <v-row align="center">
                  <!-- Timeframe -->
                  <v-col cols="12" xl="4" order-xl="1">
                    <v-btn-toggle
                      v-model="timeframe"
                      :class="{ 'justify-space-between': $vuetify.display.xs }"
                      mandatory
                      class="elevation-0 transparent"
                      @update:model-value="refresh()"
                    >
                      <v-btn
                        v-for="item in timeframeItems"
                        :key="item.value"
                        :value="item"
                        :class="{ 'px-3': $vuetify.display.smAndUp }"
                        large
                        text
                      >
                        {{ item.description }}
                      </v-btn>
                    </v-btn-toggle>
                  </v-col>
                </v-row>
              </v-container>
            </v-card-text>

            <!-- Backtest Chart -->
            <v-card-text>
              <div id="backtest_chart" />
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Backtest Table -->
      <v-row>
        <v-col cols="12">
          <v-card>
            <v-card-text>
              <v-data-table
                id="backtest-table"
                :headers="backtestTableHeaders"
                :items="backtestTableItems"
                hide-default-footer
              >
                <template #items="props">
                  <td>{{ item['name'] }}</td>
                  <td :class="[item['YTD'] > 0 ? 'text-green' : 'text-red']">
                    {{ percen(item['YTD']) }}
                  </td>
                  <td :class="[item['1Y'] > 0 ? 'text-green' : 'text-red']">
                    {{ percent(item['1Y']) }}
                  </td>
                  <td :class="[item['2Y'] > 0 ? 'text-green' : 'text-red']">
                    {{ percent(item['2Y']) }}
                  </td>
                  <td :class="[item['3Y'] > 0 ? 'text-green' : 'text-red']">
                    {{ percent(item['3Y']) }}
                  </td>
                  <td :class="[item['5Y'] > 0 ? 'text-green' : 'text-red']">
                    {{ percent(item['5Y']) }}
                  </td>
                  <td :class="[item['7Y'] > 0 ? 'text-green' : 'text-red']">
                    {{ percent(item['7Y']) }}
                  </td>
                  <td :class="[item['10Y'] > 0 ? 'text-green' : 'text-red']">
                    {{ percent(item['10Y']) }}
                  </td>
                  <td :class="[item['Total Return'] > 0 ? 'text-green' : 'text-red']">
                    {{ percent(item['Total Return']) }}
                  </td>
                  <td :class="[item['Annualized'] > 0 ? 'text-green' : 'text-red']">
                    {{ percent(item['Annualized']) }}
                  </td>
                  <td :class="[item['Std Dev'] > 0 ? 'text-green' : 'text-red']">
                    {{ percent(item['Std Dev']) }}
                  </td>
                  <td>{{ percent(item['Sortino']) }}</td>
                  <td>{{ percent(item['Max Drawdown']) }}</td>
                </template>
              </v-data-table>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <v-row v-if="results && results.portfolio.total_winners">
        <v-col cols="12">
          <v-card>
            <v-card-text>
              <v-table>
                <thead>
                  <tr>
                    <th class="text-left">Completed Trade(s) Statistics</th>
                    <th class="text-left"># Trades</th>
                    <th class="text-left">Avg Days Held</th>
                    <th class="text-left">Avg Perf Per Trade</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Winners</td>
                    <td>{{ results.portfolio.total_winners }}</td>
                    <td>{{ results.portfolio.avg_held_winners }}</td>
                    <td>{{ percent(results.portfolio.avg_perf_winners * 100.0) }}</td>
                  </tr>
                  <tr>
                    <td>Losers</td>
                    <td>{{ results.portfolio.total_losers }}</td>
                    <td>{{ results.portfolio.avg_held_losers }}</td>
                    <td>{{ percent(results.portfolio.avg_perf_losers * 100.0) }}</td>
                  </tr>
                </tbody>
              </v-table>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Ending Portfolio -->
      <v-row>
        <v-col cols="12">
          <v-card>
            <v-card-text class="pa-6 bg-grey-darken-3 text-white">
              <h2 class="text-h5 mb-2 d-inline-flex align-center">
                <img
                  src="https://d27mjrcvcy56qq.cloudfront.net/images/ico_graph.png"
                  class="mr-2"
                />
                Ending Portfolio
              </h2>
              <div>
                Click the "Download Backtest Data to Excel" button above to see all stock activity
              </div>
              <v-dialog v-model="CreateWatchlistDialog" width="500">
                <template #activator="{ props }">
                  <v-btn
                    :small="$vuetify.display.xs"
                    target="_blank"
                    v-bind="props"
                    color="primary"
                    class="ml-0"
                  >
                    Create a Watchlist From Ending Portfolio
                  </v-btn>
                </template>

                <v-card>
                  <v-card-title class="text-h5 bg-grey-lighten-2">
                    Create Stock Watchlist
                  </v-card-title>

                  <v-card-text>
                    <v-alert density="compact" type="info">
                      The number of stocks that will be added to the watchlist will be limited by
                      your subscription status. Your current limit is {{ limit }} per watchlist
                    </v-alert>

                    <v-alert v-if="stocksTableItems.length == 0" density="compact" type="warning">
                      No stocks were matched
                    </v-alert>
                    <v-text-field
                      v-model="newWatchlist.description"
                      hide-details
                      clearable
                      autofocus
                      autocorrect="off"
                      spellcheck="false"
                      class="d-inline-flex pt-0"
                      style="min-width: 300px"
                      @keypress.enter="addWatchlist(stocksTableItems)"
                    />
                  </v-card-text>

                  <v-divider></v-divider>

                  <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn
                      color="primary"
                      variant="outlined"
                      @click="CreateWatchlistDialog = false"
                    >
                      Cancel
                    </v-btn>
                    <v-btn
                      color="primary"
                      variant="outlined"
                      :disabled="
                        newWatchlist.description.trim().length == 0 || stocksTableItems.length == 0
                      "
                      @click="addWatchlist(stocksTableItems)"
                    >
                      Create Watchlist
                    </v-btn>
                  </v-card-actions>
                </v-card>
              </v-dialog>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Stocks Table & Ending Sector Allocation chart -->
      <v-row mb-2>
        <!-- Stocks Table -->
        <v-col cols="12" xl="6">
          <v-card>
            <v-card-text>
              <v-data-table
                id="backtest-stocks-table"
                :headers="stocksTableHeaders"
                :items="stocksTableItems"
                hide-default-footer
                items-per-page="-1"
              >
                <template #item="{ item }">
                  <!-- Default row -->
                  <tr v-if="!item.rowType">
                    <td>
                      <a
                        :href="`/stock/${item.stock_permalink}`"
                        :title="item.stock_name"
                        class="font-weight-bold"
                        target="_blank"
                        v-text="item.symbol"
                      />
                      {{ item.stock_name }}
                      <span v-if="item.is_short"> (short) </span>
                    </td>
                    <td>
                      <span v-if="item.percent_of_portfolio && item.percent_of_portfolio > 0">
                        {{ percent(GetCashPercentOfPortfolio(item) * 100.0) }}
                      </span>
                    </td>
                    <td :class="[GetTransactionTotalReturn(item) > 0 ? 'text-green' : 'text-red']">
                      <span v-if="!item.is_hedge">
                        {{ percent(GetTransactionTotalReturn(item)) }}
                      </span>
                    </td>
                    <td>{{ item.first_purchase_date }}</td>
                  </tr>

                  <!-- Add the extra `Cash balance` row on the bottom -->
                  <tr v-if="item.rowType === 'cashBalance'">
                    <td>
                      Cash balance of
                      <span class="font-weight-medium">
                        {{ currency(Math.round(results.portfolio.cash_on_hand * 100) / 100) }}
                      </span>
                    </td>
                    <td>
                      {{
                        percent(GetCashPercentOfPortfolio(results.portfolio.cash_on_hand) * 100.0)
                      }}
                    </td>
                    <td />
                    <td />
                  </tr>
                  <!-- Add the extra `Cash balance` row on the bottom -->
                  <tr v-if="item.rowType === 'marginBalance'">
                    <td>
                      Margin Reserve of
                      <span class="font-weight-medium">
                        {{ currency(Math.round(results.portfolio.margin_reserve * 100) / 100) }}
                      </span>
                    </td>
                    <td>
                      {{
                        percent(GetCashPercentOfPortfolio(results.portfolio.margin_reserve) * 100.0)
                      }}
                    </td>
                    <td />
                    <td />
                  </tr>
                </template>
              </v-data-table>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- Ending Sector Allocation chart -->
        <v-col cols="12" xl="6">
          <v-card>
            <v-card-text>
              <div id="backtest_sector_chart" />
            </v-card-text>
          </v-card>
        </v-col>

        <!-- Ending Market Cap chart -->
        <v-col cols="12" xl="6">
          <v-card>
            <v-card-text>
              <div id="backtest_mc_chart" />
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12">
          <v-card>
            <v-card-text class="pa-6 bg-grey-darken-3 text-white">
              <h2 class="text-h5 mb-2 d-inline-flex align-center">
                <img
                  src="https://d27mjrcvcy56qq.cloudfront.net/images/ico_graph.png"
                  class="mr-2"
                />
                Today's Trading Ideas
              </h2>
              <div>Trades scheduled to be executed during the next trading session.</div>
              <v-dialog v-model="CreateWatchlistDialog2" width="500">
                <template #activator="{ props }">
                  <v-btn
                    :small="$vuetify.display.xs"
                    target="_blank"
                    v-bind="props"
                    color="primary"
                    class="ml-0"
                  >
                    Create a Watchlist From Today's Trading Ideas
                  </v-btn>
                </template>

                <v-card>
                  <v-card-title class="text-h5 bg-grey-lighten-2">
                    Create Stock Watchlist
                  </v-card-title>

                  <v-card-text>
                    <v-alert density="compact" type="info">
                      The number of stocks that will be added to the watchlist will be limited by
                      your subscription status. Your current limit is {{ limit }} per watchlist
                    </v-alert>

                    <v-alert v-if="todaysIdeasItems.length == 0" density="compact" type="warning">
                      No stocks were matched
                    </v-alert>
                    <v-text-field
                      v-model="newWatchlist.description"
                      hide-details
                      clearable
                      autofocus
                      autocorrect="off"
                      spellcheck="false"
                      class="d-inline-flex pt-0"
                      style="min-width: 300px"
                      @keypress.enter="addWatchlist(todaysIdeasItems)"
                    />
                  </v-card-text>

                  <v-divider></v-divider>

                  <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn
                      color="primary"
                      variant="outlined"
                      @click="CreateWatchlistDialog2 = false"
                    >
                      Cancel
                    </v-btn>
                    <v-btn
                      color="primary"
                      variant="outlined"
                      :disabled="
                        newWatchlist.description.trim().length == 0 || todaysIdeasItems.length == 0
                      "
                      @click="addWatchlist(todaysIdeasItems)"
                    >
                      Create Watchlist
                    </v-btn>
                  </v-card-actions>
                </v-card>
              </v-dialog>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Stocks Table & Ending Sector Allocation chart -->
      <v-row mb-2>
        <!-- Stocks Table -->
        <v-col cols="12" xl="6">
          <v-card>
            <v-card-text>
              <v-data-table
                id="todays-ideas-table"
                :headers="todaysIdeasHeaders"
                :items="todaysIdeasItems"
                hide-default-footer
                items-per-page="-1"
              >
                <template #item="{ item }">
                  <!-- Default row -->
                  <tr v-if="!item.rowType">
                    <td>
                      <a
                        :href="`/stock/${item.stock_permalink}`"
                        :title="item.stock_name"
                        class="font-weight-bold"
                        target="_blank"
                        v-text="item.symbol"
                      />
                      {{ item.stock_name }}
                      <span v-if="item.is_short"> (short) </span>
                    </td>
                    <td>
                      {{ item.transaction_type }}
                    </td>
                    <td>
                      {{ item.description }}
                      <div v-if="!item.cancelled">
                        Estimated Shares Involved: {{ item.shares_involved }}, Estimated PPS of ${{
                          item.transaction_pps
                        }}
                        <span v-if="item.percent_of_portfolio"
                          >which is {{ item.percent_of_portfolio * 100.0 }}% of account equity</span
                        >
                      </div>
                    </td>
                  </tr>
                </template>
              </v-data-table>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <!-- Moving Averages dialog -->
    <v-dialog v-model="movingAveragesDialog" persistent width="600px">
      <v-card>
        <v-card-title class="text-h6 primary text-white"> Configure Moving Averages </v-card-title>
        <v-card-text>
          <v-text-field
            v-model.number="maToAdd"
            :rules="[
              value =>
                !moving_averages.some(ma => ma.months === value) ||
                `${value} month moving average already added`
            ]"
            type="number"
            placeholder="Add an X month moving average"
            solo
            class="mb-2"
            @keydown.enter="AddMovingAverage()"
          >
            <template #prepend-inner>
              <v-icon color="success" @click="AddMovingAverage()">
                {{ mdiPlus }}
              </v-icon>
            </template>
          </v-text-field>
          <v-card v-if="moving_averages && moving_averages.length > 0">
            <v-list class="py-0">
              <template v-for="(ma, i) in moving_averages" :key="`${i}-${ma.description}`">
                <v-divider v-if="i !== 0" />

                <v-list-item>
                  <v-list-item-title>
                    {{ ma.description }}
                  </v-list-item-title>

                  <template #append>
                    <v-btn icon @click="DeleteMovingAverage(ma)">
                      <v-icon color="error">
                        {{ mdiClose }}
                      </v-icon>
                    </v-btn>
                  </template>
                </v-list-item>
              </template>
            </v-list>
          </v-card>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn color="primary" text @click="CloseMovingAverageDialog()"> Close </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
// Modules
import Highcharts from 'highcharts'
import last from 'lodash/last' // returns the last element of a given array
import { mapStoreState, mapFields } from '@/store/helpers'
import { useUserStore, useAppStore, useWwStore } from '@/store'
import api from '@/store/api'
import { useGoTo } from 'vuetify'
// Mixins
import FormattersMixin from '@/mixins/formatters'
import { mdiClose, mdiPlus } from '@mdi/js'

export default {
  mixins: [FormattersMixin],

  setup() {
    const goTo = useGoTo()
    return { goTo }
  },

  data: () => ({
    mdiClose,
    mdiPlus,
    movingAveragesDialog: false,
    results: null,
    criteria: {},
    CreateWatchlistDialog: false,
    CreateWatchlistDialog2: false,
    newWatchlist: { id: null, description: 'New Watchlist', visibility_type_id: 0, stocks: [] },
    lowest_y: 0,
    highest_y: 0,
    timeframe: null,
    timeframeItems: [
      {
        description: 'YTD',
        value: 1,
        requires_subscription: false,
        time: 1
      },
      {
        description: '1y',
        value: 2,
        requires_subscription: false,
        time: 12
      },
      {
        description: '2y',
        value: 8,
        requires_subscription: false,
        time: 24
      },
      {
        description: '3y',
        value: 3,
        requires_subscription: true,
        time: 36
      },
      {
        description: '5y',
        value: 4,
        requires_subscription: true,
        time: 60
      },
      {
        description: '7y',
        value: 5,
        requires_subscription: true,
        time: 84
      },
      {
        description: '10y',
        value: 6,
        requires_subscription: true,
        time: 120
      },
      {
        description: 'Max',
        value: 0,
        requires_subscription: true,
        time: 0
      }
    ],
    chart: {
      xAxis: null,
      comparisonIndex: null,
      showPercentLong: true,
      showLog: false,
      longMatrix: null,
      shortMatrix: null
    },
    chartIntervalItems: [
      {
        value: 'yearly_entries',
        description: 'Yearly'
      },

      {
        value: 'monthly_entries',
        description: 'Monthly'
      }
    ],
    chartData: [],
    indices: [],
    maToAdd: null,
    moving_averages: [],
    starting_record: null,
    hedged_matrix: null,
    ending_hedged_transaction: null,
    is_compound_chart: true,
    backtestTableHeaders: [
      { title: null, key: 'name', sortable: false },
      { title: 'YTD', key: 'YTD', sortable: false },
      { title: '1Y', key: '1Y', sortable: false },
      { title: '2Y', key: '2Y', sortable: false },
      { title: '3Y', key: '3Y', sortable: false },
      { title: '5Y', key: '5Y', sortable: false },
      { title: '7Y', key: '7Y', sortable: false },
      { title: '10Y', key: '10Y', sortable: false },
      { title: 'Total Return', key: 'Total Return', sortable: false },
      { title: 'Annualized', key: 'Annualized', sortable: false },
      { title: 'Std Dev', key: 'Std Dev', sortable: false },
      { title: 'Sortino', key: 'Sortino', sortable: false },
      { title: 'Max Drawdown', key: 'Max Drawdown', sortable: false }
    ],
    stocksTableHeaders: [
      { title: 'Stock Name', key: 'Stock Name', sortable: false },
      { title: '% of Portfolio', key: '% of Portfolio', sortable: false },
      { title: 'Total Return', key: 'Total Return', sortable: false },
      { title: 'Original Purchase Date', key: 'Original Purchase Date', sortable: false }
    ],
    todaysIdeasHeaders: [
      { title: 'Stock Name', key: 'Stock Name', sortable: false },
      { title: 'Action', key: '% of Portfolio', sortable: false },
      { title: 'Description', key: 'Total Return', sortable: false }
    ]
  }),

  computed: {
    ...mapStoreState({
      limit: state => state.user.limit
    }),
    backtestTableItems() {
      return [
        {
          name: this.hedged_matrix ? 'Unhedged Portfolio' : 'Whale(s)',
          YTD: this.percent(last(this.starting_record.yearly_entries)[3] * 100),
          '1Y': this.percent(this.GetYearlyPerformance(this.starting_record, 1)),
          '2Y': this.percent(this.GetYearlyPerformance(this.starting_record, 2)),
          '3Y': this.percent(this.GetYearlyPerformance(this.starting_record, 3)),
          '5Y': this.percent(this.GetYearlyPerformance(this.starting_record, 5)),
          '7Y': this.percent(this.GetYearlyPerformance(this.starting_record, 7)),
          '10Y': this.percent(this.GetYearlyPerformance(this.starting_record, 10)),
          'Total Return': this.percent(this.starting_record.performance * 100),
          Annualized: this.percent(this.starting_record.annualized_performance * 100),
          'Std Dev': this.percent(this.starting_record.standard_deviation * 100.0),
          Sortino: this.percent(this.starting_record.sortino),
          'Max Drawdown': this.percent(this.starting_record.max_drawdown * 100.0)
        },
        {
          name: this.chart.comparisonIndex.description,
          YTD: this.percent(last(this.chart.comparisonIndex.yearly_entries)[3] * 100),
          '1Y': this.percent(this.GetYearlyPerformance(this.chart.comparisonIndex, 1)),
          '2Y': this.percent(this.GetYearlyPerformance(this.chart.comparisonIndex, 2)),
          '3Y': this.percent(this.GetYearlyPerformance(this.chart.comparisonIndex, 3)),
          '5Y': this.percent(this.GetYearlyPerformance(this.chart.comparisonIndex, 5)),
          '7Y': this.percent(this.GetYearlyPerformance(this.chart.comparisonIndex, 7)),
          '10Y': this.percent(this.GetYearlyPerformance(this.chart.comparisonIndex, 10)),
          'Total Return': this.percent(this.chart.comparisonIndex.performance * 100),
          Annualized: this.percent(this.chart.comparisonIndex.annualized_performance * 100),
          'Std Dev': this.percent(this.chart.comparisonIndex.standard_deviation * 100.0),
          Sortino: this.percent(this.chart.comparisonIndex.sortino),
          'Max Drawdown': this.percent(this.chart.comparisonIndex.max_drawdown * 100.0)
        }
      ]
    },

    stocksTableItems() {
      return [
        ...this.results.portfolio.holdings,
        { rowType: 'cashBalance' }
        //  { rowType: 'marginBalance' }
      ]
    },
    todaysIdeasItems() {
      return [...this.results.todays_trading_ideas]
    }
  },

  methods: {
    clearResults() {
      this.results = null
    },
    genResults(results, criteria) {
      this.results = results
      this.criteria = criteria
      this.indices = [] // this.results.performance_matrices.slice(1) // slice removes 'Whales' entry

      if (this.results.performance_matrices.length > 0) {
        this.starting_record = this.results.performance_matrices[0]
      }
      this.chart.longMatrix = null
      this.chart.shortMatrix = null
      if (this.results.performance_matrices.length > 1) {
        for (var i = 0; i < this.results.performance_matrices.length - 1; i++) {
          var m = this.results.performance_matrices[i]
          if (
            this.criteria.general.comp_index &&
            this.criteria.general.comp_index.id == m.index_id
          ) {
            // if (m.description == 'S&P 500 Total Return Index') {
            this.chart.comparisonIndex = m
            this.indices.push(m)
          } else if (
            !this.criteria.general.comp_index &&
            m.description == 'S&P 500 Total Return Index'
          ) {
            this.chart.comparisonIndex = m
            this.indices.push(m)
          } else if (m.description == 'Long HPR') {
            this.chart.longMatrix = m
            this.chart.longMatrix.data = []
            //  this.indices.splice(this.indices.indexOf(this.chart.longMatrix),1)
          } else if (m.description == 'Short HPR') {
            this.chart.shortMatrix = m
            this.chart.shortMatrix.data = []
            //  this.indices.splice(this.indices.indexOf(this.chart.shortMatrix),1)
          } else if (m.description == 'Cumulative Perf') {
            //do nothing
          } else {
            this.indices.push(m)
          }
        }
      }

      this.timeframe = this.timeframeItems[7]
      this.chart.xAxis = this.chartIntervalItems[1]

      this.$nextTick(() => {
        this.refresh()
        this.loadSectorChart('backtest_sector_chart', this.results.portfolio.holdings)

        this.loadMcChart('backtest_mc_chart', this.results.portfolio.holdings)

        this.$nextTick(function () {
          setTimeout(
            () =>
              this.goTo('#results-header', {
                offset: 20,
                duration: 2000
              }),
            100
          )
        })
      })
    },

    refresh() {
      this.chartData = []
      var max = 0
      var timeframe = this.timeframe.time
      var today = new Date()
      var earliestDate = new Date(today.getFullYear(), today.getMonth() - timeframe)

      if (this.timeframe.value === 1) {
        earliestDate = new Date(today.getFullYear(), 0, 1)
      }

      var hedgingHighlight = {
        name: 'Market Timing is on',
        color: '#FFFF66',
        area: true,
        data: [],
        turboThreshold: 8000,
        yAxis: 2,
        tooltip: {
          pointFormatter() {
            var point = this
            return point.series.name
          }
        },
        interactive: false,
        useInteractiveGuideline: false,
        lineinteractive: false
      } // https://djaodjin.com/blog/two-background-colors-nvd3-line-chart.blog.html
      var maxY = 0
      // or bar chart - create new entry see http://nvd3.org/examples/linePlusBar.html
      var a = []

      var longPercentage = {
        name: 'Avg % Invested Long',
        data: [],
        area: false,
        color: '#ff3333',
        type: 'line',
        yAxis: 1,
        step: 'left',
        dashStyle: 'ShortDash',
        lineWidth: 1,
        tooltip: false,
        interactive: false,
        useInteractiveGuideline: false,
        lineinteractive: false
      }
      var exposure = {
        name: 'Avg Exposure',
        data: [],
        area: false,
        visible: false,
        // color: '#ff3333',
        type: 'line',
        yAxis: 1,
        step: 'left',
        dashStyle: 'ShortDash',
        lineWidth: 1,
        tooltip: false,
        interactive: false,
        useInteractiveGuideline: false,
        lineinteractive: false
      }
      var efficiency = {
        name: 'Capital Efficiency',
        data: [],
        area: false,
        visible: false,
        // color: '#ff3333',
        type: 'line',
        yAxis: 1,
        step: 'left',
        dashStyle: 'ShortDash',
        lineWidth: 1,
        tooltip: false,
        interactive: false,
        useInteractiveGuideline: false,
        lineinteractive: false
      }

      if (this.results.ma && this.results.ma.length > 0) {
        var ma = {
          data: [],
          type: 'line',
          color: '#004B8C',
          yAxis: 2,
          name: 'Index Moving Avg',
          area: false,
          tooltip: {
            pointFormat: ''
          }
        }
        var indexprice = {
          data: [],
          type: 'line',
          color: '#8BA9D0',
          yAxis: 2,
          name: 'Index Price',
          area: false,
          tooltip: {
            pointFormat: ''
          }
        }
        for (var i = 0; i < this.results.ma.length; i++) {
          var indexma = this.results.ma[i]
          var d3data = {
            x: null,
            y: indexma.value,
            hedged: false
          }
          var s = indexma.date

          if (!isNaN(s) || s.length === 4) s = new Date(parseInt(s), 1, 1)
          else {
            var darray = s.split('-')
            s = new Date(darray[0], parseInt(darray[1]) - 1, darray[2])
          }

          d3data.x = s.getTime()
          ma.data.push(d3data)
          indexprice.data.push({
            x: s.getTime(),
            y: indexma.price
          })
          hedgingHighlight.data.push({
            x: s.getTime(),
            y: indexma.on ? parseFloat(indexma.value) : null
          })
        }
        this.chartData.push(ma)
        this.chartData.push(indexprice)
        this.chartData.push(hedgingHighlight)
      }

      if (this.results.performance_matrices.length > 1) {
        a.push(this.results.performance_matrices[0])
        if (this.chart.longMatrix) {
          a.push(this.chart.longMatrix)
        }
        if (this.chart.shortMatrix) {
          a.push(this.chart.shortMatrix)
        }
        a.push(this.chart.comparisonIndex)
      }

      if (this.chart.xAxis.value === 'monthly_entries') {
        var colors = ['#FFCC0F', '#C79F25', '#705A11', '#8BA9D0', '#6A90C1', '#066CA9', '#004B8C']
        for (var movingindex = 0; movingindex < this.moving_averages.length; movingindex++) {
          ma = this.moving_averages[movingindex]
          ma.data = []
          ma.name = ma.description
          if (movingindex < colors.length) ma.color = colors[movingindex]
        }
      }

      for (i = 0; i < a.length; i++) {
        var p = a[i]
        if (p.months) {
          continue
        }
        var data = []
        var tempdata = []
        var last = null

        if (this.chart.xAxis.value === 'quarterly_entries') {
          tempdata = p['monthly_entries']
        } else {
          tempdata = p[this.chart.xAxis.value] // yearly_entries, monthly_entries, etc
        }

        if (timeframe !== 0) {
          // strip out data earlier than earliestDate
          for (var j = 0; j < tempdata.length; j++) {
            var entry = tempdata[j]
            darray = null
            darray = entry[1].split('-')
            s = new Date(darray[0], darray[1] - 1, darray[2])
            if (s.getTime() >= earliestDate.getTime()) {
              data.push(entry)
            } else last = entry
          }
        } else {
          data = tempdata
        }

        var obj = {
          name: p.description,
          data: [],
          area: true
        }

        if (i === 0) {
          obj.color = '#57aea5' // '#96d8cc'
        } else if (i === 1 && this.hedged_matrix) {
          obj.color = '#00cc00'
        } else {
          obj.color = '#111'
        }

        var compound = 0

        var start = p.starting_balance

        if (timeframe !== 0) {
          if (last) start = last[2]
          else start = data[0][2]
        }

        for (j = 0; j < data.length; j++) {
          entry = data[j]
          d3data = {
            x: null,
            y: null,
            hedged: false
          }
          s = entry[0]

          if (!isNaN(s) || s.length === 4) s = new Date(parseInt(s), 1, 1)
          else {
            darray = s.split('-')
            s = new Date(darray[0], parseInt(darray[1]) - 1, darray[2])
          }

          d3data.x = s.getTime()

          if (p == this.chart.longMatrix || p == this.chart.shortMatrix) {
            obj.area = false
            obj.color = p == this.chart.longMatrix ? '#C79F25' : '#33CC33' // #FFFF33
            obj.type = 'column'
            obj.yAxis = 1
            obj.visible = false
            d3data.y = parseFloat(entry[3]) * 100.0
          } else {
            if (this.is_compound_chart) {
              compound = ((entry[2] - start) / start) * 1.0
              d3data.y = compound * 100.0
            } else d3data.y = parseFloat(entry[3]) * 100.0
          }

          if (i === 0) {
            var percentry = { x: d3data.x, y: entry[4] * 100.0 }
            longPercentage.data.push(percentry)
            exposure.data.push({ x: d3data.x, y: entry[7] * 100.0 })
            efficiency.data.push({ x: d3data.x, y: entry[8] * 100.0 })
            if (this.chart.longMatrix) {
              // var longentry=this.chart.longMatrix;
            }
            if (this.chart.shortMatrix) {
              // this.chart.shortMatrix.data=[];
            }
          }

          obj.data.push(d3data)

          if (d3data.y > max) max = d3data.y
          if (d3data.y > maxY) maxY = d3data.y

          if (i === 0 && this.chart.xAxis.value === 'monthly_entries') {
            for (movingindex = 0; movingindex < this.moving_averages.length; movingindex++) {
              ma = this.moving_averages[movingindex]
              // ma will be obj with description and months, values
              // take average of current-ma.months and add to ma.values
              if (j >= ma.months) {
                // whale ma needs to be perf for past X months
                var base = data[j - ma.months][2]
                var maperf = (data[j][2] - base) / base
                // hedging comp ma needs to be avg of sums of perfs for past 12 monts
                // with base being mv from 12 months ago
                ma.data.push({
                  x: d3data.x,
                  y: maperf * 100.0
                })
              } else {
                ma.data.push({
                  x: d3data.x,
                  y: null
                })
              }
            }
          }

          if (this.chart.xAxis.value === 'quarterly_entries') {
            j += 2
            if (j + 1 >= data.length) j = data.length - 1
          }
        }
        if (obj.data.length > 0) {
          this.chartData.push(obj)
        }
      }

      if (this.chart.showPercentLong && longPercentage && longPercentage.data.length > 0) {
        this.chartData.push(longPercentage)
      }
      if (exposure.data.length > 0) {
        this.chartData.push(exposure)
      }

      if (efficiency.data.length > 0) {
        this.chartData.push(efficiency)
      }

      if (this.chart.xAxis.value === 'monthly_entries') {
        for (movingindex = 0; movingindex < this.moving_averages.length; movingindex++) {
          ma = this.moving_averages[movingindex]
          this.chartData.push(ma)
        }
      }
      if (this.chart.showLog) {
        var cum_index = -1
        for (var seriesindex = 0; seriesindex < this.chartData.length; seriesindex++) {
          var ser = this.chartData[seriesindex]
          if (ser.name == 'Cumulative Perf') {
            cum_index = seriesindex
            break
          }
        }
        this.convert_to_log(this.chartData[cum_index], this.chartData[cum_index + 3])
      }
      var logBase = 10
      var that = this
      var y_axes = [
        {
          // type: 'logarithmic',
          // tickInterval:0.4,
          // allowNegativeLog: true,
          title: {
            text: '% Performance'
          },
          labels: {
            formatter() {
              if (that.chart.showLog) {
                var converted = Math.pow(10, this.value)
                //  if (that.lowest_y<0 && converted<Math.abs(that.lowest_y))
                converted += that.lowest_y
                return converted.toFixed(2) * 1.0 + '%'
              } else {
                return this.value.toFixed(2) * 1.0 + '%'
              }
            }
          }
        },
        {
          title: {
            text: '% Invested'
          },
          labels: {
            enabled: true,
            formatter() {
              return this.value * 1.0 + '%'
            }
          },
          opposite: true
        } /*,
                       {
                       type: 'logarithmic',
                       allowNegativeLog: true,
                         title: {
                           text: null
                         },
                         labels: {
                           enabled: false,
                           formatter () {
                             return this.value// * 100.0 + '%'
                           }
                         },
                         opposite: true
                       } */
      ]

      if (this.results.ma && this.results.ma.length > 0) {
        y_axes.push({
          //   type: 'logarithmic',
          //    tickInterval:1,
          //   softMin:-0.2,
          //  softMax:1,
          //  allowNegativeLog: true,
          title: {
            text: null
          },
          labels: {
            enabled: false
            /* formatter () {
                                                 return this.value * 100.0 + '%'
                                               } */
          }
        })
      }

      ;(function (H) {
        H.addEvent(H.Axis, 'init', function (e) {
          this.allowNegativeLog = e.userOptions.allowNegativeLog
        })

        /*      H.Axis.prototype.log2lin = function (num) {
        var isNegative = num < 0,
            adjustedNum = Math.abs(num),
            result;
        if (adjustedNum < 10) {
            adjustedNum += (10 - adjustedNum) / 10;
        }
        result = Math.log(adjustedNum) / Math.LN10;
        return isNegative ? -result : result;
    };
    H.Axis.prototype.lin2log = function (num) {
        var isNegative = num < 0,
            absNum = Math.abs(num),
            result = Math.pow(10, absNum);
        if (result < 10) {
            result = (10 * (result - 1)) / (10 - 1);
        }
        return isNegative ? -result : result;
    }; */

        // Override conversions
        H.wrap(H.Axis.prototype, 'log2lin', function (proceed, num) {
          if (!this.allowNegativeLog) {
            return proceed.call(this, num)
          }

          var isNegative = num < 0
          var adjustedNum = Math.abs(num)
          var result
          if (adjustedNum < logBase) {
            adjustedNum += (logBase - adjustedNum) / logBase
          }
          result = Math.log(adjustedNum) / Math.log(logBase) // Math.LN10;
          return isNegative ? -result : result
        })
        H.wrap(H.Axis.prototype, 'lin2log', function (proceed, num) {
          if (!this.allowNegativeLog) {
            return proceed.call(this, num)
          }

          var isNegative = num < 0
          var absNum = Math.abs(num)
          var result = Math.pow(logBase, absNum)
          if (result < logBase) {
            result = (logBase * (result - 1)) / (logBase - 1)
          }
          return isNegative ? -result : result
        })
      })(Highcharts)

      Highcharts.chart('backtest_chart', {
        chart: {
          type: 'area',
          height: 500,
          backgroundColor: '#FFFFFF'
        },
        title: {
          text: ''
        },
        subtitle: {},
        xAxis: {
          allowDecimals: false,
          labels: {
            formatter() {
              var d = new Date(this.value)
              if (this.chart.xAxis.value === 'yearly_entries') return d.getFullYear()
              else return d.getFullYear() + '-' + (d.getMonth() + 1)
            }
          }
        },
        yAxis: y_axes,
        tooltip: {
          shared: true,
          formatter() {
            var points = this.points
            var pointsLength = points.length
            var d = new Date(points[0].key)

            var tooltipMarkup = pointsLength
              ? '<span style="font-size: 10px">' +
                d.getFullYear() +
                '-' +
                (d.getMonth() + 1) +
                '</span><br/>'
              : ''
            var index
            var yValueKwh

            for (index = 0; index < pointsLength; index += 1) {
              var ser = points[index].series
              if (ser.name == 'Index Price' || ser.name == 'Index Moving Avg') {
                continue
              } else if (ser.name == 'Market Timing is on') {
                tooltipMarkup +=
                  '<span style="color:' +
                  points[index].series.color +
                  '">\u25CF</span> ' +
                  points[index].series.name
              } else {
                yValueKwh = 0 // (points[index].y * 1.0).toFixed(2)
                if (that.chart.showLog && ser.name != 'Avg % Invested Long') {
                  var converted = Math.pow(10, points[index].y)
                  // if (that.lowest_y<0 && converted<Math.abs(that.lowest_y))
                  converted += that.lowest_y
                  yValueKwh = converted * 1.0
                } else {
                  yValueKwh = points[index].y
                }
                yValueKwh = yValueKwh.toFixed(2)
                tooltipMarkup +=
                  '<span style="color:' +
                  points[index].series.color +
                  '">\u25CF</span> ' +
                  points[index].series.name +
                  ': <b>' +
                  yValueKwh +
                  '%</b><br/>'
              }
            }

            return tooltipMarkup
          }
        },
        credits: {
          enabled: false
        },
        plotOptions: {
          area: {
            // pointStart: 1940,
            marker: {
              enabled: false,
              symbol: 'circle',
              radius: 2,
              states: {
                hover: {
                  enabled: true
                }
              }
            }
          }
        },
        series: this.chartData
      })
    },

    loadSectorChart(element, data) {
      var chartSeries = []
      var series = {
        name: 'Sectors',
        data: []
      }
      var h = {
        cashOnHand: {
          name: 'Cash on Hand',
          y: Math.round(parseFloat(this.results.portfolio.cash_on_hand) * 100) / 100
        }
      }
      var totalMV = parseFloat(this.results.portfolio.cash_on_hand)
      /* if (this.results.portfolio.margin_reserve){
        h["marginReserve"]={
          name: 'Margin Reserve',
          y: Math.round(parseFloat(this.results.portfolio.margin_reserve) * 100) / 100
        }
      } */

      for (var i = 0; i < data.length; i++) {
        var stock = data[i]

        totalMV += Math.abs(
          parseFloat(stock.current_mv + (stock.margin_reserve ? stock.margin_reserve : 0))
        )
      }

      for (i = 0; i < data.length; i++) {
        stock = data[i]

        if (stock.shares === 0) continue
        if (!h[stock.sector]) {
          h[stock.sector] = {
            name: stock.sector,
            y: 0
          }
        }
        h[stock.sector].y += Math.abs(stock.current_mv)
      }

      for (var propertyName in h) {
        if (propertyName === undefined) continue
        var entry = h[propertyName]
        if (!entry.y) continue
        entry.y = (entry.y / totalMV) * 100.0
        series.data.push(entry)
      }

      chartSeries.push(series)

      Highcharts.chart(element, {
        chart: {
          type: 'pie',
          backgroundColor: '#FFFFFF'
        },
        title: {
          text: 'Sector Allocation'
        },
        xAxis: {
          type: 'category'
        },
        yAxis: {
          min: 0,
          title: {
            text: 'Sector Percentage'
          }
        },
        tooltip: {
          pointFormat:
            '<span style="color:{series.color}">{series.name}</span>:  ({point.percentage:.0f}%)<br/>'
        },
        plotOptions: {
          pie: {
            allowPointSelect: true,
            cursor: 'pointer',
            dataLabels: {
              enabled: false
            },
            showInLegend: true
          }
        },
        credits: {
          enabled: false
        },
        series: chartSeries
      })
    },
    get_market_cap_description(mc_id) {
      var description = ''
      var marketCaps = [
        { name: 'Over $200 billion', id: 1 },
        { name: '$100 billion - $200 billion', id: 2 },
        { name: '$50 billion - $100 billion', id: 3 },
        { name: '$20 billion - $50 billion', id: 4 },
        { name: '$10 billion - $20 billion', id: 5 },
        { name: '$2 billion - $10 billion', id: 6 },
        { name: '$1 billion - $2 billion', id: 7 },
        { name: '$500 million - $1 billion', id: 8 },
        { name: '$250 million - $500 million', id: 9 },
        { name: 'Below $250 million', id: 10 }
      ]
      for (var i = 0; i < marketCaps.length; i++) {
        if (marketCaps[i].id == mc_id) {
          description = marketCaps[i].name
          break
        }
      }
      return description
    },
    loadMcChart(element, data) {
      var chartdata = []
      var h = {}
      for (var i = 0; i < data.length; i++) {
        var stock = data[i]

        if (!stock.stock_data) continue
        if (!h[stock.stock_data.market_cap_id]) {
          h[stock.stock_data.market_cap_id] = {
            name: this.get_market_cap_description(stock.stock_data.market_cap_id),
            y: 0
          }
        }
        h[stock.stock_data.market_cap_id].y += Math.abs(stock.current_mv)
      }

      for (var propertyName in h) {
        if (propertyName === undefined) continue
        var entry = h[propertyName]
        if (!entry.y) continue
        chartdata.push(entry)
      }

      Highcharts.chart(element, {
        chart: {
          plotBackgroundColor: null,
          plotBorderWidth: null,
          plotShadow: false,
          type: 'pie'
        },
        credits: {
          enabled: false
        },
        title: {
          text: 'Concentration by Stock Market Cap'
        },
        tooltip: {
          pointFormat: '{series.name}: <b>{point.percentage:.1f}%</b>'
        },
        plotOptions: {
          pie: {
            allowPointSelect: true,
            cursor: 'pointer',
            dataLabels: {
              enabled: true,
              format: '<b>{point.name}</b>: {point.percentage:.1f} %',
              style: {
                color: (Highcharts.theme && Highcharts.theme.contrastTextColor) || 'black'
              }
            }
          }
        },
        series: [
          {
            name: '% Concentration',
            colorByPoint: true,
            data: chartdata
          }
        ]
      })
    },

    setTimeframe(timeframe) {
      this.timeframeProxy = timeframe.value
      this.refresh()
    },

    GetYearlyPerformance(record, numYears) {
      if (!record) return ''
      var perf = 0
      var start = 0
      var end = 0
      var numMonths = numYears * 12
      var months = record.monthly_entries
      if (months.length < numMonths) return 'N/A'
      if (months[months.length - numMonths].ending_balance) {
        start = months[months.length - numMonths].ending_balance
        end = months[months.length - 1].ending_balance
      } else {
        start = months[months.length - numMonths][2]
        end = months[months.length - 1][2]
      }
      perf = (end - start) / start
      return perf * 100.0
    },

    GetTransactionTotalReturn(transaction) {
      if (!transaction) return ''

      var basis = parseFloat(transaction.avg_pps) * parseFloat(transaction.shares)
      var gain = parseFloat(transaction.realized_gain) + parseFloat(transaction.unrealized_gain)
      if (transaction.is_short) {
        basis = parseFloat(this.results.portfolio.cash_on_hand)
        this.results.portfolio.holdings.forEach(holding => {
          if (!transaction.is_short)
            basis +=
              Math.abs(parseFloat(holding.current_mv)) +
              (holding.margin_reserve ? holding.margin_reserve : 0)
        })
      }
      return (gain / basis) * 100.0
    },

    GetCashPercentOfPortfolio(t) {
      var total = parseFloat(this.results.portfolio.cash_on_hand)
      //  if (this.results.portfolio.margin_reserve)
      // total+=this.results.portfolio.margin_reserve

      this.results.portfolio.holdings.forEach(holding => {
        if (!t.is_short)
          total +=
            Math.abs(parseFloat(holding.current_mv)) +
            (holding.margin_reserve ? holding.margin_reserve : 0)
      })

      var ret = (Math.abs(t.current_mv) + (t.margin_reserve ? t.margin_reserve : 0)) / total
      if (t.is_short) ret = ret * -1.0
      return ret
    },

    AddMovingAverage() {
      if (!this.moving_averages.some(ma => ma.months === this.maToAdd)) {
        var sortedArray = this.moving_averages
        sortedArray.push({
          description: `${this.maToAdd} month moving average`,
          months: this.maToAdd
        })
        this.moving_averages = sortedArray.sort(
          (ma1, ma2) => parseInt(ma1.months) > parseInt(ma2.months)
        )
        this.maToAdd = null
      }
    },

    DeleteMovingAverage(ma) {
      var index = this.moving_averages.indexOf(ma)
      if (index >= 0) {
        this.moving_averages.splice(index, 1)
      }
    },

    CloseMovingAverageDialog() {
      this.movingAveragesDialog = false
      this.refresh()
    },
    convert_to_log(series_data1, series_data2) {
      this.highest_y = 0
      this.lowest_y = 0
      for (var series_index = 0; series_index < series_data1.data.length; series_index++) {
        var point = series_data1.data[series_index]
        if (point.y < this.lowest_y) {
          this.lowest_y = point.y
        }
        if (point.y > this.highest_y) {
          this.highest_y = point.y
        }
      }
      for (series_index = 0; series_index < series_data2.data.length; series_index++) {
        point = series_data2.data[series_index]
        if (point.y < this.lowest_y) {
          this.lowest_y = point.y
        }
        if (point.y > this.highest_y) {
          this.highest_y = point.y
        }
      }
      if (this.lowest_y < 0) {
        this.lowest_y += -1
      }
      for (series_index = 0; series_index < series_data1.data.length; series_index++) {
        point = series_data1.data[series_index]
        // convert to log  result = Math.log(adjustedNum) / Math.log(logBase);//Math.LN10;
        //  point.y=Math.log(point.y+(this.lowest_y<0 ? Math.abs(this.lowest_y) : 0));
        point.y = Math.log(point.y + (this.lowest_y < 0 ? Math.abs(this.lowest_y) : 0)) / Math.LN10
      }
      for (series_index = 0; series_index < series_data2.data.length; series_index++) {
        point = series_data2.data[series_index]
        // convert to log  result = Math.log(adjustedNum) / Math.log(logBase);//Math.LN10;
        point.y = Math.log(point.y + (this.lowest_y < 0 ? Math.abs(this.lowest_y) : 0)) / Math.LN10
      }
    },
    addWatchlist(items) {
      //verify name and at least one stock
      if (this.newWatchlist.description.trim().length == 0 || items.length == 0) return
      this.saving = true
      for (var i = 0; i < items.length; i++) {
        if (this.newWatchlist.stocks.length >= this.limit) {
          break
        }
        this.newWatchlist.stocks.push({ id: items[i].stock_id })
      }
      let url = 'watchlists'
      api
        .post(url, this.newWatchlist)
        .then(response => {
          useUserStore().loadWatchlists()
          useAppStore().showNotification({ type: 'success', text: 'Watchlist has been saved' })
          this.newWatchlist = {
            id: null,
            description: 'New Watchlist',
            visibility_type_id: 0,
            stocks: []
          }
          this.CreateWatchlistDialog = false
          this.CreateWatchlistDialog2 = false
        })
        .catch(error => {
          useAppStore().showNotification({ type: 'error', text: error.message })
        })
        .then(() => (this.saving = false))
    }
  }
}
</script>

<style lang="scss">
#backtest-table,
#backtest-stocks-table {
  thead th {
    color: rgba(0, 0, 0, 0.87);
  }
}
</style>
